import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ErpLink } from "./ErpLink";
import { requestWeeklyPack, type WeeklyPack } from "../lib/api";
import { markdownToHtml } from "../lib/markdown";
import { FeedbackControl } from "./FeedbackControl";
import { ProvenanceBadge } from "./ui";

/** "Weekly pack" button + modal (CEO-09 / CEO-13): facts gathered as the person, narrated through the gateway,
 * saved as a Draft Insight for review. */
export function WeeklyPackButton({ context }: { context: Record<string, unknown> }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pack, setPack] = useState<WeeklyPack | null>(null);
  const [error, setError] = useState<string | null>(null);

  const generate = async () => {
    setOpen(true);
    setBusy(true);
    setError(null);
    try {
      setPack(await requestWeeklyPack(i18n.language, context));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = () => { if (pack) void navigator.clipboard?.writeText(pack.markdown); };

  return (
    <>
      <button onClick={() => (pack ? setOpen(true) : void generate())} disabled={busy}
        className="flex items-center gap-1.5 rounded-md border border-violet-300 px-3 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-50 disabled:opacity-60">
        <span aria-hidden="true">✦</span> {t("pack.button")}
      </button>
      {open && (
        <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4 print:static print:bg-white print:p-0" onClick={() => setOpen(false)}>
          <div className="pack-print my-6 w-full max-w-3xl rounded-2xl bg-white shadow-2xl print:my-0 print:max-w-none print:rounded-none print:shadow-none" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-brand-grey-light px-6 py-3 print:hidden">
              <div className="flex items-center gap-2 font-heading text-sm font-semibold"><span className="text-violet-600">✦</span> {t("pack.title")} {pack && <ProvenanceBadge source={pack.source === "ai" ? "ai" : "rule"} />}</div>
              <div className="flex items-center gap-2 text-xs">
                {pack && <button onClick={() => void generate()} disabled={busy} className="rounded border border-brand-grey-light px-2 py-1 hover:border-brand-primary disabled:opacity-50">{t("pack.regenerate")}</button>}
                {pack && <button onClick={copy} className="rounded border border-brand-grey-light px-2 py-1 hover:border-brand-primary">{t("pack.copy")}</button>}
                {pack && <button onClick={() => window.print()} className="rounded border border-brand-grey-light px-2 py-1 hover:border-brand-primary">{t("pack.print")}</button>}
                <button onClick={() => setOpen(false)} aria-label="close" className="rounded px-2 py-1 text-brand-grey hover:bg-brand-grey-light">✕</button>
              </div>
            </div>
            <div className="px-6 py-4">
              {busy && (
                <div className="py-10 text-center text-sm text-brand-grey"><span className="animate-pulse">{t("pack.generating")}</span></div>
              )}
              {error && <div role="alert" className="rounded border border-status-red/40 bg-red-50 p-3 text-sm text-status-red">{t("pack.failed")}<div className="mt-1 text-xs text-brand-grey">{error.slice(0, 200)}</div></div>}
              {pack && !busy && (
                <>
                  <article className="prose-pack text-sm leading-relaxed text-brand-charcoal" dangerouslySetInnerHTML={{ __html: markdownToHtml(pack.markdown) }} />
                  <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-brand-grey-light pt-3 text-[11px] text-brand-grey print:hidden">
                    <span>{t("assistant.toolsUsed")}:</span>
                    {pack.evidence.map((e) => <span key={e.ref} className="rounded bg-[#F6F8FA] px-1.5 py-0.5">{e.ref}</span>)}
                    <span className="ml-auto">{pack.model?.replace(/^ollama\//, "") ?? t("provenance.rule")} · {(pack.latency_ms / 1000).toFixed(0)} s</span>
                  </div>
                  <FeedbackControl interactionId={pack.interaction_id} className="mt-2 print:hidden" />
                  {pack.insight && (
                    <div className="mt-2 text-xs print:hidden">
                      {t("pack.saved")} <ErpLink>{pack.insight}</ErpLink> · {t("pack.review")}
                    </div>
                  )}
                  <p className="mt-3 text-[10px] text-brand-grey">{t("assistant.disclaimer")}</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
